import {
  Dropdown,
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
  Tag,
  TextInput,
  Toggle,
} from '@carbon/react';
import type { FieldEditorProps } from '@pos/admin-ui';
import { brandThemeSchema, build, failures, fromBrandSite, withSiteRoles, type BrandTheme, type ContrastCheck } from '@pos/brand-tokens';
import { useTranslation } from '@pos/i18n';
import { useMemo, type CSSProperties } from 'react';
import './brandSites.scss';

const HEX = /^#[0-9A-Fa-f]{6}$/;
const FONT_PAIRS = ['be-vietnam-pro', 'inter', 'nunito', 'roboto-slab+inter'] as const;
const RADII = ['sharp', 'soft', 'round'] as const;
const TINTS = ['warm', 'cool', 'pure'] as const;

function parse(value: unknown): { theme: Record<string, unknown>; broken: boolean } {
  if (typeof value !== 'string' || !value.trim()) return { theme: {}, broken: false };
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? { theme: parsed as Record<string, unknown>, broken: false } : { theme: {}, broken: true };
  } catch {
    return { theme: {}, broken: true };
  }
}

/**
 * A brand site's theme (FR-ONL-17, "Customer design system"): the few inputs a brand sets, edited as fields instead of JSON,
 * with a live preview built by @pos/brand-tokens, the same generator the website uses, and its contrast checks. The server
 * checks the theme again when it is saved (BrandTheme).
 */
export function ThemeField({ value, invalidText, values, onChange }: FieldEditorProps) {
  const { t } = useTranslation();
  const { theme, broken } = parse(value);
  const set = (key: keyof BrandTheme, next: string | undefined) => {
    const updated: Record<string, unknown> = Object.fromEntries(Object.entries(theme).filter(([k]) => k !== key));
    if (next !== undefined && next !== '') updated[key] = next;
    onChange(Object.keys(updated).length ? JSON.stringify(updated) : '{}');
  };
  const text = (key: 'primary' | 'accent') => (typeof theme[key] === 'string' ? (theme[key] as string) : '');
  const siteName = typeof values.siteName === 'string' && values.siteName.trim() ? values.siteName : t('brandSites.theme.previewName');

  // The preview only uses a theme that parses: a colour still being typed doesn't break it.
  const raw = typeof value === 'string' ? value : '';
  const preview = useMemo(() => {
    const current = parse(raw).theme;
    const parsed = brandThemeSchema.safeParse(
      Object.fromEntries(Object.entries(current).filter(([k, v]) => !(k === 'primary' || k === 'accent') || (typeof v === 'string' && HEX.test(v)))),
    );
    const input = fromBrandSite(siteName, parsed.success ? parsed.data : {});
    const tokens = withSiteRoles(build(input));
    return { tokens, problems: failures(input, tokens), radius: input.radius ?? 'soft', font: input.fonts.body ?? '' };
  }, [raw, siteName]);

  const light = preview.tokens.light;
  const radius = { sharp: '4px', soft: '8px', round: '16px' }[preview.radius];
  // The brand's own colours, as the website will paint them: values from the generator, not Carbon tokens.
  const vars = {
    '--preview-bg': light.bg,
    '--preview-surface': light['surface-raised'],
    '--preview-text': light.text,
    '--preview-muted': light['text-muted'],
    '--preview-primary': light.primary,
    '--preview-on-primary': light['on-primary'],
    '--preview-subtle': light['primary-subtle'],
    '--preview-on-subtle': light['on-primary-subtle'],
    '--preview-link': light.link,
    '--preview-header': light.header,
    '--preview-on-header': light['on-header'],
    '--preview-accent': light.accent ?? light.primary,
    '--preview-on-accent': light['on-accent'] ?? light['on-primary'],
    '--preview-radius': radius,
    '--preview-font': preview.font,
  } as CSSProperties;

  return (
    <fieldset className="pos-theme">
      <legend className="pos-theme__legend">{t('md.fields.theme')}</legend>
      {broken && <InlineNotification kind="warning" lowContrast hideCloseButton title={t('brandSites.theme.broken')} />}
      {invalidText && <InlineNotification kind="error" lowContrast hideCloseButton title={invalidText} />}
      <div className="pos-theme__fields">
        {(['primary', 'accent'] as const).map((key) => (
          <div key={key} className="pos-theme__colour">
            <TextInput
              id={`theme-${key}`}
              labelText={t(`brandSites.theme.${key}`)}
              helperText={t(`brandSites.theme.${key}Help`)}
              placeholder="#C73F00"
              value={text(key)}
              invalid={text(key) !== '' && !HEX.test(text(key))}
              invalidText={t('brandSites.theme.hex')}
              onChange={(e) => set(key, e.target.value.trim())}
            />
            <span
              className="pos-theme__swatch"
              aria-hidden="true"
              style={{ background: HEX.test(text(key)) ? text(key) : 'transparent' } as CSSProperties}
            />
          </div>
        ))}
        <Dropdown
          id="theme-fontPair"
          titleText={t('brandSites.theme.fontPair')}
          label={t('brandSites.theme.fontPair')}
          items={[...FONT_PAIRS]}
          itemToString={(item: (typeof FONT_PAIRS)[number] | null) => (item ? t(`brandSites.theme.fonts.${item}`) : '')}
          selectedItem={FONT_PAIRS.find((f) => f === theme.fontPair) ?? 'be-vietnam-pro'}
          onChange={({ selectedItem }: { selectedItem: (typeof FONT_PAIRS)[number] | null }) => set('fontPair', selectedItem ?? undefined)}
        />
        <RadioButtonGroup
          legendText={t('brandSites.theme.radius')}
          name="theme-radius"
          valueSelected={(theme.radius as string | undefined) ?? 'soft'}
          onChange={(next) => set('radius', String(next))}
        >
          {RADII.map((r) => (
            <RadioButton key={r} id={`theme-radius-${r}`} labelText={t(`brandSites.theme.radii.${r}`)} value={r} />
          ))}
        </RadioButtonGroup>
        <RadioButtonGroup
          legendText={t('brandSites.theme.neutralTint')}
          name="theme-neutralTint"
          valueSelected={(theme.neutralTint as string | undefined) ?? 'warm'}
          onChange={(next) => set('neutralTint', String(next))}
        >
          {TINTS.map((n) => (
            <RadioButton key={n} id={`theme-tint-${n}`} labelText={t(`brandSites.theme.tints.${n}`)} value={n} />
          ))}
        </RadioButtonGroup>
        <Toggle
          id="theme-darkMode"
          labelText={t('brandSites.theme.darkMode')}
          labelA={t('brandSites.theme.darkOff')}
          labelB={t('brandSites.theme.darkAuto')}
          toggled={theme.darkMode !== 'off'}
          onToggle={(on) => set('darkMode', on ? 'auto' : 'off')}
        />
      </div>

      <section className="pos-theme__preview" aria-label={t('brandSites.theme.preview')} style={vars}>
        <div className="pos-theme__preview-header">{siteName}</div>
        <div className="pos-theme__preview-body">
          <p className="pos-theme__preview-title">Phở bò tái</p>
          <p className="pos-theme__preview-muted">{t('brandSites.theme.sampleText')}</p>
          <div className="pos-theme__preview-row">
            <span className="pos-theme__preview-button">{t('brandSites.theme.sampleButton')}</span>
            <span className="pos-theme__preview-chip">{t('brandSites.theme.sampleChip')}</span>
            <span className="pos-theme__preview-badge">{t('brandSites.theme.sampleBadge')}</span>
          </div>
          <span className="pos-theme__preview-link">{t('brandSites.theme.sampleLink')}</span>
        </div>
      </section>

      {preview.problems.length === 0 ? (
        <Tag type="green">{t('brandSites.theme.contrastOk')}</Tag>
      ) : (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={t('brandSites.theme.contrastProblems', { count: preview.problems.length })}
          subtitle={preview.problems.map((p: ContrastCheck) => `${p.fg} / ${p.bg}: ${p.ratio.toFixed(2)}:1 (${p.minimum}:1)`).join(' · ')}
        />
      )}
    </fieldset>
  );
}
